import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../config/api';
import { useAuth } from './AuthContext';
import { useLivePoll } from '../hooks/useLivePoll';

export interface AppNotification {
  id: string;
  user_notification_id: string;
  title: string;
  message: string;
  type: string;
  cafe_name: string;
  read: boolean;
  created_at: string;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  showPanel: boolean;
  setShowPanel: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  deleteNotification: (id: string, e?: React.MouseEvent) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const staffConsole = pathname === '/barista' || pathname.startsWith('/barista/');
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [showPanel, setShowPanel] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || staffConsole) {
      setNotifications([]);
      return;
    }
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 401) {
        setNotifications([]);
      }
    }
  }, [user, staffConsole]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useLivePoll(refresh, 4000, Boolean(user) && !staffConsole);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await axios.put(`/api/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.user_notification_id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await axios.delete(`/api/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.user_notification_id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        showPanel,
        setShowPanel,
        refresh,
        markAsRead,
        deleteNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
